<template>
  <TTabsToggle
    :header="$t('forms.settings_element_button')"
    no-move
    :changed="wasChanged"
    data-testid="button-settings"
  >
    <menu-content-container border-full :padding-left-right="8">
      <TLabel
        :changed="align.changed"
        :tooltip="$t('forms.reset_value')"
        @reset="align.reset"
        >{{ $t("forms.button_align") }}</TLabel
      >
      <content-row justify-between>
        <TButtonGroup data-testid="align-button-group">
          <TGroupButton
            :disabled="fullWidth.model"
            data="left"
            data-testid="align-left"
            small
            :active="align.model === 'left'"
            @button-clicked="align.model = 'left'"
          >
            <AligmentLeft />
          </TGroupButton>
          <TGroupButton
            :disabled="fullWidth.model"
            data="center"
            data-testid="align-center"
            small
            :active="align.model === 'center'"
            @button-clicked="align.model = 'center'"
          >
            <AligmentCenter />
          </TGroupButton>
          <TGroupButton
            :disabled="fullWidth.model"
            data="right"
            data-testid="align-right"
            small
            :active="align.model === 'right'"
            @button-clicked="align.model = 'right'"
          >
            <AligmentRight />
          </TGroupButton>
        </TButtonGroup>
        <t-switch
          v-model="fullWidth.model"
          :label="$t('forms.full_width')"
          data-testid="full-width-switch"
          :changed="fullWidth.changed"
          @reset="fullWidth.reset"
        ></t-switch>
      </content-row>
    </menu-content-container>

    <menu-content-container border-full :padding-left-right="4">
      <FontSizeColor
        tag="mj-button"
        :label="$t('forms.font_settings')"
        no-border
      />
      <content-row :margin-top="8" class="!block">
        <TButtonGroup
          data-testid="text-align-button-group"
          :label="$t('forms.button_align')"
          :changed="textAlign.changed"
          flex
          @reset="textAlign.reset"
        >
          <TGroupButton
            data="left"
            data-testid="text-align-left"
            small
            :active="textAlign.model === 'left'"
            @button-clicked="textAlign.model = 'left'"
          >
            <TextLeft />
          </TGroupButton>
          <TGroupButton
            data="center"
            data-testid="text-align-center"
            small
            :active="textAlign.model === 'center'"
            @button-clicked="textAlign.model = 'center'"
          >
            <TextCenter />
          </TGroupButton>
          <TGroupButton
            data="right"
            data-testid="text-align-right"
            small
            :active="textAlign.model === 'right'"
            @button-clicked="textAlign.model = 'right'"
          >
            <TextRight />
          </TGroupButton>
        </TButtonGroup>
      </content-row>
    </menu-content-container>

    <menu-content-container
      :header="$t('forms.button_style')"
      :padding-left-right="8"
    >
      <content-row class="!items-end">
        <color-picker
          :label="$t('forms.button_color')"
          data-testid="button-color"
          position="left"
          :default-color="bgColor.model"
          :changed="bgColor.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @color-change="bgColor.model = $event"
          @reset="bgColor.reset"
        ></color-picker>
        <TButton
          v-if="!gradient.model"
          data-testid="add-gradient"
          @click="addGradient"
        >
          <template #icon>
            <svg
              width="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19.958 3C20.3669 3 20.7597 3.16205 21.0488 3.45117C21.3379 3.74029 21.5 4.13312 21.5 4.54199V19.958C21.5 20.3669 21.3379 20.7597 21.0488 21.0488C20.7597 21.3379 20.3669 21.5 19.958 21.5H4.54199C4.13312 21.5 3.74029 21.3379 3.45117 21.0488C3.16205 20.7597 3 20.3669 3 19.958V4.54199C3 4.13312 3.16205 3.74029 3.45117 3.45117C3.74029 3.16205 4.13312 3 4.54199 3H19.958ZM10.708 4.54199V7.625H7.625V10.708H10.708V13.792H7.625V16.875H10.708V19.958H13.792V16.875H16.875V19.958H19.958V4.54199H16.875V7.625H13.792V4.54199H10.708ZM13.792 16.875H10.708V13.792H13.792V16.875ZM16.875 13.792H13.792V10.708H16.875V13.792ZM13.792 10.708H10.708V7.625H13.792V10.708Z"
                fill="currentColor"
              />
            </svg>
          </template>
          {{ $t("forms.add_gradient") }}
        </TButton>
      </content-row>
      <content-row class="!block" data-testid="gradient-form">
        <TTabsToggle
          v-if="gradient.model"
          :header="$t('forms.gradient')"
          :changed="gradient.changed"
          default-open
          no-move
          show-remove
          can-be-reset
          @remove-block="gradient.model = undefined"
          @reset="gradient.reset"
        >
          <menu-content-container border-full :padding-left-right="8">
            <TGradient v-model="gradient.model" />
          </menu-content-container>
        </TTabsToggle>
      </content-row>

      <content-row :margin-bottom="16">
        <t-note>{{ $t("layout.background_gradient_text") }}</t-note>
      </content-row>
    </menu-content-container>

    <border-form
      :border="border.model"
      :default-border="border.defaultValue"
      data-testid="button-border-form"
      :allow-directions="false"
      :margin-top="12"
      :changed="border.changed"
      :padding-left-right="8"
      @borderChanged="onBorderChanged"
      @reset="border.reset"
    />

    <div>
      <menu-content-container
        :header="$t('forms.button_hover')"
        :border-full="!hover"
        :padding-left-right="8"
      >
        <template #header-action>
          <div
            class="flex cursor-pointer items-center font-inter text-xs font-medium leading-6 text-accent-1 transition duration-300 hover:opacity-80"
            data-testid="toggle-hover-button"
            @click="toggleHover"
          >
            <div>
              {{ !hover ? $t("forms.add_hover") : $t("forms.remove_hover") }}
            </div>
            <PlusIcon v-if="!hover" />
            <Minus v-else />
          </div>
        </template>
        <transition name="slide-fade-borders" mode="out-in">
          <div v-if="hover">
            <content-row>
              <color-picker
                :label="$t('forms.button_color_hover')"
                data-testid="hover-button-color"
                position="left"
                :default-color="hoverBgColor"
                :changed="isHoverPropChanged('background-color')"
                :reset-tooltip="$t('forms.reset_value')"
                @color-change="hoverBgColor = $event"
                @reset="resetHoverProp('background-color')"
              ></color-picker>
              <color-picker
                :label="$t('forms.button_text_color_hover')"
                data-testid="hover-button-text-color"
                position="right"
                :default-color="hoverColor"
                :changed="isHoverPropChanged('color')"
                :reset-tooltip="$t('forms.reset_value')"
                @color-change="hoverColor = $event"
                @reset="resetHoverProp('color')"
              ></color-picker>
            </content-row>
            <content-row class="!block">
              <TButton
                v-if="!hoverGradient"
                data-testid="add-hover-gradient"
                @click="addGradientHover"
              >
                <template #icon>
                  <svg
                    width="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19.958 3C20.3669 3 20.7597 3.16205 21.0488 3.45117C21.3379 3.74029 21.5 4.13312 21.5 4.54199V19.958C21.5 20.3669 21.3379 20.7597 21.0488 21.0488C20.7597 21.3379 20.3669 21.5 19.958 21.5H4.54199C4.13312 21.5 3.74029 21.3379 3.45117 21.0488C3.16205 20.7597 3 20.3669 3 19.958V4.54199C3 4.13312 3.16205 3.74029 3.45117 3.45117C3.74029 3.16205 4.13312 3 4.54199 3H19.958ZM10.708 4.54199V7.625H7.625V10.708H10.708V13.792H7.625V16.875H10.708V19.958H13.792V16.875H16.875V19.958H19.958V4.54199H16.875V7.625H13.792V4.54199H10.708ZM13.792 16.875H10.708V13.792H13.792V16.875ZM16.875 13.792H13.792V10.708H16.875V13.792ZM13.792 10.708H10.708V7.625H13.792V10.708Z"
                      fill="currentColor"
                    />
                  </svg>
                </template>
                {{ $t("forms.add_gradient") }}
              </TButton>
              <TTabsToggle
                v-if="hoverGradient"
                :header="$t('forms.gradient_hover')"
                :changed="isHoverPropChanged('gradient')"
                data-testid="hover-gradient-form"
                default-open
                no-move
                show-remove
                can-be-reset
                @remove-block="hoverGradient = 'none'"
                @reset="resetHoverProp('gradient')"
              >
                <menu-content-container border-full :padding-left-right="8">
                  <TGradient v-model="hoverGradient" />
                </menu-content-container>
              </TTabsToggle>
            </content-row>
          </div>
        </transition>

        <content-row v-if="!hover">
          <t-note>{{ $t("layout.hover_text") }}</t-note>
        </content-row>
      </menu-content-container>
      <transition name="slide-fade-borders" mode="out-in">
        <border-form
          v-if="hover"
          :label="$t('forms.button_border_hover')"
          :border="hoverBorder"
          :default-border="border.model"
          data-testid="hover-button-border-form"
          :allow-directions="false"
          :margin-top="12"
          :padding-left-right="8"
          :changed="isHoverPropChanged('border')"
          @borderChanged="onHoverBorderChanged"
          @reset="resetHoverProp('border')"
        >
          <content-row :margin-top="12">
            <t-note>{{ $t("layout.hover_text") }}</t-note>
          </content-row>
        </border-form>
      </transition>
    </div>

    <borderRadiusForm
      data-testid="button-border-radius"
      :label="$t('forms.button_radius')"
      external-usage
      :external-border-radius="borderRadius.model"
      :changed="borderRadius.changed"
      :default-value="borderRadius.defaultValue"
      :padding-left-right="8"
      @borderRadiusChanged="borderRadius.model = $event"
      @reset="borderRadius.reset"
    ></borderRadiusForm>

    <paddingForm
      data-testid="button-padding-form"
      :label="$t('forms.button_padding')"
      :input-label="$t('forms.padding_label')"
      :toggle-label="$t('forms.independent_paddings')"
      prefix="inner-"
      external-usage
      :external-padding="innerPadding.model"
      :changed="innerPadding.changed"
      :default-value="innerPadding.defaultValue"
      :padding-left-right="8"
      @padding-changed="innerPadding.model = $event"
      @reset="innerPadding.reset"
    ></paddingForm>

    <paddingForm
      data-testid="button-margin-form"
      :label="$t('forms.button_margin')"
      external-usage
      :external-padding="padding.model"
      :changed="padding.changed"
      :default-value="padding.defaultValue"
      :padding-left-right="8"
      @padding-changed="padding.model = $event"
      @reset="padding.reset"
    ></paddingForm>
  </TTabsToggle>
</template>

<script setup lang="ts">
  import colorPicker from "@/forms/elements/colorPicker.vue";
  import borderForm from "@/forms/borderForm.vue";
  import borderRadiusForm from "@/forms/borderRadiusForm.vue";
  import paddingForm from "@/forms/paddingForm.vue";
  import FontSizeColor from "@/forms/components/FontSizeColor/FontSizeColor.vue";
  import TGradient from "@/components/TGradient/TGradient.vue";
  import { useBlockAttr } from "@/composables/useBlockAttr";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import {
    AligmentLeft,
    AligmentCenter,
    AligmentRight,
    TextLeft,
    TextCenter,
    TextRight,
    PlusIcon,
    Minus,
  } from "@topol/icons";
  import {
    TTabsToggle,
    TButtonGroup,
    TGroupButton,
    TButton,
    TLabel,
    TNote,
    TSwitch,
  } from "@topol/twig-ui";
  import { computed, watch } from "vue";

  const mjmlStore = useMjmlStore();
  const { str, nullable, bool, anyChanged, anyPropChanged } = useBlockAttr({
    mode: "global",
    block: "mj-button",
  });

  // --- Standard attributes via composable ---
  const bgColor = str("background-color");
  const align = str("align", "center");
  const textAlign = str("text-align", "center");
  const gradient = nullable("gradient");
  const border = nullable("border");
  const borderRadius = nullable("border-radius");
  const innerPadding = nullable("inner-padding");
  const padding = nullable("padding");
  const fullWidth = bool("width", "100%", "auto");

  watch(
    () => fullWidth.model,
    (val) => {
      if (val) {
        mjmlStore.updateBlockAttrGlobally({
          block: "mj-button",
          prop: "align",
          value: "center",
        });
      }
    }
  );

  // --- Border helper ---
  function onBorderChanged({ border: newBorder }: { border: string }) {
    const value =
      newBorder === undefined || newBorder.startsWith("0px")
        ? undefined
        : newBorder;
    border.model = value;
  }

  // --- Gradient helper ---
  function addGradient() {
    gradient.model = "linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)";
  }

  // --- Hover (custom — uses different store methods) ---
  const hover = computed({
    get() {
      return mjmlStore.getBaseAttr("mj-button", "hover");
    },
    set(val?: any) {
      mjmlStore.updateBlockAttrGlobally({
        block: "mj-button",
        prop: "hover",
        value: val,
      });
    },
  });

  const hoverBgColor = computed({
    get() {
      return hover.value?.["background-color"] || bgColor.model || "#e85034";
    },
    set(val: string) {
      hover.value = { ...hover.value, "background-color": val };
    },
  });

  const hoverColor = computed({
    get() {
      return hover.value?.color || str("color").model;
    },
    set(val: string) {
      hover.value = { ...hover.value, color: val };
    },
  });

  const hoverBorder = computed({
    get() {
      return hover.value?.border || border.model;
    },
    set(val: string) {
      hover.value = { ...hover.value, border: val };
    },
  });

  const hoverGradient = computed({
    get() {
      if (hover.value?.gradient === "none") {
        return undefined;
      }
      return hover.value?.gradient || gradient.model;
    },
    set(val?: string) {
      if (!val) {
        const newHover = { ...hover.value };
        delete newHover.gradient;
        hover.value = newHover;
        return;
      }
      hover.value = { ...hover.value, gradient: val };
    },
  });

  function toggleHover() {
    hover.value = hover.value ? undefined : {};
  }

  function addGradientHover() {
    hoverGradient.value = "linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)";
  }

  function onHoverBorderChanged({ border: newBorder }: { border: string }) {
    hover.value = { ...hover.value, border: newBorder };
  }

  const hoverDefaults = computed(() => {
    return mjmlStore.getBaseAttrWithDefault("mj-button", "hover")?.defaultValue;
  });

  function isHoverPropChanged(prop: string): boolean {
    if (!hover.value) return false;
    const currentVal = hover.value[prop];
    const defaultVal = hoverDefaults.value?.[prop];
    if (defaultVal !== undefined) {
      return currentVal !== undefined && currentVal !== defaultVal;
    }
    return currentVal !== undefined;
  }

  function resetHoverProp(prop: string) {
    if (!hover.value) return;
    const defaultVal = hoverDefaults.value?.[prop];
    if (defaultVal !== undefined) {
      hover.value = { ...hover.value, [prop]: defaultVal };
    } else {
      const newHover = { ...hover.value };
      delete newHover[prop];
      hover.value = newHover;
    }
  }

  // --- Was changed ---
  const fontChanged = anyPropChanged(
    "color",
    "font-size",
    "font-family",
    "font-weight",
    "font-style",
    "text-decoration",
    "text-transform",
    "letter-spacing",
    "line-height"
  );

  const wasChanged = computed(() => {
    return (
      fontChanged.value ||
      anyChanged(
        bgColor,
        gradient,
        align,
        textAlign,
        border,
        borderRadius,
        innerPadding,
        padding,
        fullWidth
      ).value
    );
  });
</script>
